import React, { useEffect, useMemo, useRef, useState } from "react";
import ObservationItemWrapper from "./ObservationItemWrapper";
import { fetchObservations } from "src/shared/services/src";
import { handleError, tryCatchWrapper } from "src/app/utilities/helpers";
import { DATE_FORMATS } from "src/shared/config/src/lib/constants";
import { dateFormatter } from "src/shared/utils/src";
import { ObservationDetailsLoader } from "src/app/components";
import { ChildrenStoreData } from "src/shared/types/src";
import { toast } from "react-toastify";


type FiltersType = Partial<{
  fromDate: Date | undefined;
  toDate: Date | undefined;
}>;

type TimelinePagesWrapperProps = {
  filters: FiltersType;
  isDataLoaded: (value: boolean) => void;
  onError: () => void;
  child: ChildrenStoreData
  onDobFetch: (dob: string) => void;
  isObservationAvailable?: (available: boolean) => void;
};

const EymemoryTimelinePagesWrapper: React.FC<TimelinePagesWrapperProps> = ({
  filters,
  isDataLoaded,
  onError,
  child,
  onDobFetch,
  isObservationAvailable
}) => {
  const [pageData, setPageData] = useState<Record<number, any[]>>({});
  const [loadingPages, setLoadingPages] = useState<Set<number>>(new Set());
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [isAlreadyLoaded, setAlreadyLoaded] = useState(false);
  const [isDataPresent, setIsDataPresent] = useState(false);
  const filtersRef = useRef<FiltersType>({
    fromDate: undefined,
    toDate: undefined,
  });
  const totalPagesRef = useRef<number | null>(totalPages);
  const [totalMediaCount, setTotalMediaCount] = useState(0);
  const [mediaLoadedCount, setMediaLoadedCount] = useState(0);

  const reset = () => {
    setLoadingPages(() => new Set());
    totalPagesRef.current = null;
    setTotalPages(() => null);
    setPageData({});
  };

  const loadPage = async (page: number) => {
    await tryCatchWrapper(
      async () => {
        const { fromDate, toDate } = filters;
        setLoadingPages((prev) => new Set(prev).add(page));
        setIsDataPresent(true);
        const res = await fetchObservations({
          childId: child.childId,
          page,
          filterObj: {
            ...(fromDate && toDate
              ? {
                from: dateFormatter(fromDate, DATE_FORMATS.YMD) || "",
                to: dateFormatter(toDate, DATE_FORMATS.YMD) || "",
              }
              : {})
          },
        });
        res?.child?.dob && onDobFetch(res?.child?.dob);
        if (res?.list?.length) {
          setPageData((prev) => ({ ...prev, [page]: res?.list }));
          const count = res?.list.reduce(
            (acc: any, obs) => acc + (obs?.media?.length || 0),
            0
          );
          setTotalMediaCount((prev) => {
            if (page === 1) {
              setMediaLoadedCount(() => 0);
              return count;
            }
            return prev + count;
          });
          if (res.meta?.last_page  && totalPagesRef.current === null) {
            totalPagesRef.current = res.meta.last_page;
            setTotalPages(res.meta.last_page);
            setIsDataPresent(true);
          }
        } else {
          // This block will run when we have empty observations list.
          setIsDataPresent(false);
          isDataLoaded(true);
        }
        isObservationAvailable?.(res?.meta?.total !== 0);
        if(res?.meta?.total === 0) {
          toast.success("No observation to print");
        }
      },
      {
        onFinally: () => {
          setLoadingPages((prev) => {
            const copy = new Set(prev);
            copy.delete(page);
            return copy;
          });
        },
        onError: (error: any) => {
          onError();
          handleError(error);
        },
      }
    );
  };

  useEffect(() => {
    loadPage(1);
    setAlreadyLoaded(true);
    filtersRef.current = filters;
    return () => {
      reset();
    };
  }, []);

  useEffect(() => {
    // After totalPages is known, load rest in parallel
    if (totalPages && Object.keys(pageData).length === 1) {
      const arr: any[] = [];
      for (let i = 2; i <= totalPages; i++) {
        arr.push(loadPage(i));
      }
      Promise.allSettled(arr).then(() => {
        if (totalMediaCount === 0) {
          isDataLoaded(true);
        }
      });
    }
  }, [totalPages]);

  useEffect(() => {
    if (totalMediaCount > 0 && mediaLoadedCount === totalMediaCount) {
      isDataLoaded(true);
    }
  }, [mediaLoadedCount, totalMediaCount]);

  const compareFilters = (
    prevFilters: FiltersType,
    newFilters: FiltersType
  ) => {
    if (
      prevFilters.fromDate?.toISOString() !== newFilters.fromDate?.toISOString()
    ) {
      return true;
    }
    if (
      prevFilters.toDate?.toISOString() !== newFilters.toDate?.toISOString()
    ) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (isAlreadyLoaded && compareFilters(filtersRef.current, filters)) {
      filtersRef.current = filters;
      setTotalMediaCount(() => 0);
      setMediaLoadedCount(() => 0);
      reset();
      isDataLoaded(false);
      loadPage(1);
    }
  }, [filters]);

  const allPages = useMemo(() => {
    if (!totalPages) return [1];
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }, [totalPages]);

  return (
    <>
      {isDataPresent && <div className="page-separator" />}
      {allPages.map((page, index) => (
        <React.Fragment key={page}>
          {loadingPages.has(page) ? (
            <>
              <ObservationDetailsLoader showFooter={false} />
              {allPages && allPages?.length - 1 !== index && (
                <div className="page-separator" />
              )}
            </>
          ) : (
            pageData[page]?.map((observation, obsIndex) => (
              <React.Fragment key={obsIndex}>
                <ObservationItemWrapper
                  observation={observation}
                  onMediaLoad={() => setMediaLoadedCount((prev) => prev + 1)}
                />
                {totalPages && !((totalPages === page) && (pageData[page]?.length - 1) == obsIndex ) && (
                  <div className="page-separator" />
                )}
              </React.Fragment>
            ))
          )}
        </React.Fragment>
      ))}
    </>
  );
};

export default EymemoryTimelinePagesWrapper;
